/**
 * ProtectedRoute Component (DC-891)
 *
 * Wraps routes that require authentication.
 * Redirects to login if user is not authenticated.
 * Optionally checks for specific roles or permissions.
 */

import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth, UserRole } from '../../contexts/AuthContext';

interface ProtectedRouteProps {
  children: React.ReactNode;
  /** Required role(s) to access this route */
  roles?: UserRole | UserRole[];
  /** Required permission to access this route */
  permission?: string;
  /** Where to redirect if not authenticated */
  redirectTo?: string;
  /** Fallback component while loading */
  loadingFallback?: React.ReactNode;
}

/**
 * ProtectedRoute - Requires authentication to access
 */
export function ProtectedRoute({
  children,
  roles,
  permission,
  redirectTo = '/login',
  loadingFallback,
}: ProtectedRouteProps) {
  const { isAuthenticated, isLoading, hasRole, hasPermission } = useAuth();
  const location = useLocation();

  // Show loading state
  if (isLoading) {
    return (
      <>
        {loadingFallback || (
          <div className="auth-loading">
            <div className="auth-loading-spinner" />
            <p>Loading...</p>
          </div>
        )}
      </>
    );
  }

  // Not authenticated - redirect to login
  if (!isAuthenticated) {
    // Save the attempted URL for redirecting after login
    return <Navigate to={redirectTo} state={{ from: location }} replace />;
  }

  // Check role requirement
  if (roles && !hasRole(roles)) {
    return <Navigate to="/access-denied" replace />;
  }

  // Check permission requirement
  if (permission && !hasPermission(permission)) {
    return <Navigate to="/access-denied" replace />;
  }

  return <>{children}</>;
}

/**
 * AdminRoute - Shorthand for routes that require admin role
 */
export function AdminRoute({
  children,
  redirectTo = '/login',
  loadingFallback,
}: Omit<ProtectedRouteProps, 'roles' | 'permission'>) {
  return (
    <ProtectedRoute
      roles="admin"
      redirectTo={redirectTo}
      loadingFallback={loadingFallback}
    >
      {children}
    </ProtectedRoute>
  );
}

/**
 * AnalystRoute - Shorthand for routes that require analyst or admin role
 */
export function AnalystRoute({
  children,
  redirectTo = '/login',
  loadingFallback,
}: Omit<ProtectedRouteProps, 'roles' | 'permission'>) {
  return (
    <ProtectedRoute
      roles={['admin', 'analyst']}
      redirectTo={redirectTo}
      loadingFallback={loadingFallback}
    >
      {children}
    </ProtectedRoute>
  );
}

/**
 * PublicOnlyRoute - Redirects to home if already authenticated (for login/register pages)
 */
interface PublicOnlyRouteProps {
  children: React.ReactNode;
  /** Where to redirect if already authenticated */
  redirectTo?: string;
}

export function PublicOnlyRoute({
  children,
  // '/', not '/home': ProfileLanding resolves the organization's experience
  // profile there (banking → /use-cases, core → /home). D-BANK-UX-02 PM #1.
  redirectTo = '/',
}: PublicOnlyRouteProps) {
  const { isAuthenticated, isLoading } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return (
      <div className="auth-loading">
        <div className="auth-loading-spinner" />
        <p>Loading...</p>
      </div>
    );
  }

  if (isAuthenticated) {
    // Check if there's a redirect URL from login state
    const from = (location.state as { from?: { pathname: string } })?.from?.pathname;
    return <Navigate to={from || redirectTo} replace />;
  }

  return <>{children}</>;
}

export default ProtectedRoute;
